import type { PreparationState, Tenant } from '@bk/contracts';
import { comuneLeggibile, denominazioneLeggibile } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { Chain, clock, Neurons, SinapsiPage, spent, Timeline, useNow } from './Sinapsi';

/**
 * La fase 0 prima della pagina «La tua azienda» (docs/mockup/percorso-sinapsi/01-conferma e 02-lavoro): il titolare
 * conferma l'identità («Sei X di Y?») mentre il sito ufficiale si cerca già; con il sì il consulente lavora e il titolare
 * vede il lettore e il contatore (art. 10.4) e i fatti già trovati con la fonte. Letto l'atto si apre la fase 0.
 */
export function PreparazionePage({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const tenant = useQuery({
    queryKey: ['tenant', tenantId, 'self'],
    queryFn: () => api<Tenant>(`/tenants/${tenantId}`),
    refetchInterval: (q) => (q.state.data?.status === 'active' || q.state.data?.status === 'failed' ? false : 1500),
  });
  const active = tenant.data?.status === 'active';
  const prep = useQuery({
    queryKey: ['tenant', tenantId, 'preparation'],
    queryFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation`),
    enabled: active,
    refetchInterval: (q) => (q.state.data?.pronta ? false : 1500),
  });
  const confirm = useMutation({
    mutationFn: (si: boolean) => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/confirm`, { method: 'POST', body: { si } }),
    onSuccess: (s, si) => {
      qc.setQueryData(['tenant', tenantId, 'preparation'], s);
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'self'] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
      // Con il no l'incarico riparte dalla partita IVA giusta: nessuna visura viene comprata (art. 7-bis.1).
      if (!si) navigate('/riparti');
    },
  });
  const retry = useMutation({
    mutationFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/retry`, { method: 'POST' }),
    onSuccess: (s) => qc.setQueryData(['tenant', tenantId, 'preparation'], s),
  });
  const retryDb = useMutation({
    mutationFn: () => api<Tenant>(`/tenants/${tenantId}/provision`, { method: 'POST' }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'self'] }),
  });
  const s = prep.data;
  const ready = !!s?.pronta;
  useEffect(() => {
    if (!ready) return;
    void qc.invalidateQueries({ queryKey: ['tenants'] });
    const timer = setTimeout(() => navigate(`/t/${tenantId}/configurazione`), 1200);
    return () => clearTimeout(timer);
  }, [ready, tenantId, qc]);
  const now = useNow(true);
  const vat = tenant.data?.vat_number ?? '';
  const ask = s?.conferma ?? null;
  const name = ask?.denominazione ? denominazioneLeggibile(ask.denominazione) : null;
  const town = ask?.comune ? comuneLeggibile(ask.comune).replace(/\s*\([A-Z]{2}\)\s*$/, '') : null;
  const short = (s?.trovati.ragione_sociale ? denominazioneLeggibile(s.trovati.ragione_sociale) : tenant.data?.name ?? '').replace(/\s+S\.[a-z.A-Z]+$/, '');
  const failed = tenant.data?.status === 'failed';
  const stopped = s?.passi.filter((p) => p.stato === 'errore') ?? [];

  // 01 — la conferma dell'identità.
  if (ask || !active || (!s && !failed)) {
    const site = s?.sito ?? null;
    return (
      <SinapsiPage glow="b" network="Rete neurale della tua azienda" state="Fase 0 · Identità" stateTone="on" onExit={{ label: 'Le tue aziende', go: () => navigate('/aziende') }}>
        <div className="sn-wrap narrow" style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
          <Chain steps={[`Partita IVA ${vat}`, 'Sei tu?', 'La visura']} at={1} />
          <div className="sn-panel lit" style={{ padding: '32px 34px', gap: 16 }}>
            <p className="sn-ey cyan">Una conferma</p>
            {ask ? (
              <>
                <h1 className="sn-h1 md">
                  Sei {name ?? 'questa impresa'}
                  {town ? ` di ${town}` : ''}?
                </h1>
                <p className="sn-lead" style={{ fontSize: 16 }}>
                  L’abbiamo trovata nel Registro Imprese con la tua partita IVA. Con il sì scarichiamo la visura camerale. Intanto stiamo già cercando il sito ufficiale, che è gratuito.
                </p>
                <div className="sn-quote">
                  <span className="sn-ey cyan" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
                    Come la scrive il Registro
                  </span>
                  {[ask.denominazione, ask.comune ? `comune ${ask.comune.toUpperCase()}` : null, `partita IVA ${vat}`].filter(Boolean).join(' · ')}
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                  <button type="button" className="sn-btn" disabled={confirm.isPending} onClick={() => confirm.mutate(true)}>
                    Sì, siamo noi
                  </button>
                  <button type="button" className="sn-btn ghost" disabled={confirm.isPending} onClick={() => confirm.mutate(false)}>
                    No, non siamo noi
                  </button>
                </div>
                {confirm.error && <p className="sn-err">{errorMessage(confirm.error)}</p>}
                <p className="sn-note">Con il no l’incarico riparte dalla partita IVA giusta: nessuna visura viene comprata.</p>
              </>
            ) : (
              <>
                <h1 className="sn-h1 md">{failed ? 'Il fascicolo non si è aperto' : 'Cerco la tua impresa nel Registro Imprese'}</h1>
                <p className="sn-lead" style={{ fontSize: 16 }}>
                  {failed ? tenant.data?.provisioning_error ?? 'Qualcosa non è andato: riprova.' : 'Creo il fascicolo dell’azienda e cerco la partita IVA nel Registro Imprese: tra un attimo ti chiedo se è la tua.'}
                </p>
                {failed && tenant.data?.role === 'owner' && (
                  <div>
                    <button type="button" className="sn-btn" disabled={retryDb.isPending} onClick={() => retryDb.mutate()}>
                      Riprova
                    </button>
                  </div>
                )}
                {!failed && (
                  <p className="sn-mono sn-cyan" style={{ fontSize: 13 }}>
                    <span className="sn-dot spin" style={{ verticalAlign: -1, marginRight: 8 }} />
                    {clock(spent(tenant.data?.created_at ?? null, null, now))}
                  </p>
                )}
              </>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <div className="sn-card">
              <span aria-hidden className={`sn-dot mark ${s?.verifica?.registro ? 'ok' : 'spin'}`} />
              <p className="sn-ey" style={{ color: s?.verifica?.registro ? '#46e0b0' : undefined, letterSpacing: '0.08em', fontSize: 11 }}>
                {s?.verifica?.registro ? 'Partita IVA verificata' : 'Verifico la partita IVA'}
              </p>
              <p style={{ marginTop: 4, fontSize: 14, color: '#c3cbe0' }}>
                {[
                  s?.verifica?.registro ? `Registro Imprese: iscritta${s.verifica.registro.rea ? `, REA ${s.verifica.registro.rea}` : ''}` : 'Registro Imprese: cerco…',
                  s?.verifica?.vies === 'attiva' ? 'VIES: attiva' : s?.verifica?.vies === 'non_abilitata' ? 'VIES: non abilitata agli scambi UE' : null,
                  'Agenzia delle Entrate: fonte da collegare',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <div className="sn-card">
              <span aria-hidden className={`sn-dot mark ${site?.in_corso ? 'spin' : site?.sito ? 'violet' : site && !site.in_corso && s?.passi.some((p) => p.passo === 'identita' && p.stato !== 'in_attesa') ? 'off' : 'off'}`} />
              <p className="sn-ey" style={{ color: '#3cf0ff', letterSpacing: '0.08em', fontSize: 11 }}>
                {site?.in_corso ? `Cerco il sito ufficiale · ${clock(spent(site.dal, null, now))}` : site?.sito ? 'Sito ufficiale trovato' : 'Cerco il sito ufficiale'}
              </p>
              <p style={{ marginTop: 4, fontSize: 14, color: '#c3cbe0' }}>
                {site?.sito ? `${site.sito.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')} · pagine in cui compare la partita IVA: ${site.pagine}` : site?.in_corso ? `Pagine in cui compare la partita IVA: ${site.pagine} trovate finora` : 'Parte appena l’impresa è trovata nel Registro, mentre aspetto il tuo sì.'}
              </p>
            </div>
          </div>
        </div>
      </SinapsiPage>
    );
  }

  // 02 — il consulente al lavoro.
  return (
    <SinapsiPage glow="c" network={`Rete neurale di ${short || 'questa azienda'}`} state={ready ? 'Fase 0 · Pronta' : 'Fase 0 · Al lavoro'} stateTone={ready ? 'ok' : 'on'} onExit={{ label: 'Le tue aziende', go: () => navigate('/aziende') }}>
      <div className="sn-wrap sn-two work">
        {s?.tempi ? (
          <Timeline
            t={s.tempi}
            eyebrow="Il consulente al lavoro"
            title={ready ? 'Conosco la tua azienda: apro la fase 0' : 'Conosco la tua azienda'}
            note={s.motore_fermo ? `${s.motore_fermo} Riprendo da solo appena il motore risponde.` : undefined}
          />
        ) : (
          <section className="sn-panel" style={{ padding: '26px 30px' }}>
            <p className="sn-ey cyan">Il consulente al lavoro</p>
            <h1 className="sn-h1 sm">Conosco la tua azienda</h1>
            <p className="sn-note">Il contatore compare tra un attimo.</p>
          </section>
        )}
        <Neurons tenantId={tenantId} />
      </div>
      {(stopped.length > 0 || s?.rifiutata) && (
        <div className="sn-wrap" style={{ paddingTop: 0 }}>
          <div className="sn-panel gold">
            {s?.rifiutata ? (
              <p className="sn-note">Hai detto che non è la tua azienda: nessuna visura è stata comprata. Riparti dalla partita IVA giusta.</p>
            ) : (
              <>
                <p className="sn-ey" style={{ color: '#ffd68c' }}>
                  Qualcosa non è riuscito
                </p>
                {stopped.map((p) => (
                  <p key={p.passo} className="sn-note">
                    {p.nota}
                  </p>
                ))}
                <div>
                  <button type="button" className="sn-btn ghost sm" disabled={retry.isPending} onClick={() => retry.mutate()}>
                    Riprova
                  </button>
                </div>
              </>
            )}
            {s?.rifiutata && (
              <div>
                <button type="button" className="sn-btn sm" onClick={() => navigate('/riparti')}>
                  Riparti dalla partita IVA
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {(tenant.error || prep.error) && (
        <div className="sn-wrap" style={{ paddingTop: 0 }}>
          <p className="sn-err">{errorMessage(tenant.error ?? prep.error)}</p>
        </div>
      )}
    </SinapsiPage>
  );
}
